import { Icons } from '../../shared/helpers/icons';
import { EScheduleTypes, ESectionTypes } from '../../shared/models/eop_types.enum';

export type EopPlanSlug = 'al' | 'ms' | 'ga';
export type EopIconKey = keyof typeof Icons;

export interface IEopPageConfig {
  title: string;
  route: string;
}

export interface IEopSidebarItem {
  title: string;
  route: string;
}

export interface IEopSidebarEntry {
  title: string;
  icon: EopIconKey;
  route?: string;
  activeRoutes?: string[];
  children?: IEopSidebarItem[];
}

export const DEFAULT_EOP_PLAN: EopPlanSlug = 'ms';

export const SUPPORTED_EOP_PLANS: EopPlanSlug[] = ['ms', 'al', 'ga'];

// Report types offered on the Drill, Training & Collaboration Reports page.
// AL uses all four; MS has no "Crisis" category; GA only logs drills.
export const REPORT_TYPES_BY_PLAN: Record<EopPlanSlug, EScheduleTypes[]> = {
  al: [EScheduleTypes.Drills, EScheduleTypes.Training, EScheduleTypes.Crisis, EScheduleTypes.Collaboration],
  ms: [EScheduleTypes.Drills, EScheduleTypes.Training, EScheduleTypes.Collaboration],
  ga: [EScheduleTypes.Drills]
};

const alSidebarEntries: IEopSidebarEntry[] = [
  { title: 'EOP Summary', route: 'summary', icon: 'Home' },
  { title: 'Introduction & Statement of Commitment', route: 'statement-of-commitment', icon: 'Handshake' },
  {
    title: 'Collaborative Planning Team',
    icon: 'BookUser',
    activeRoutes: ['system-school-safety-planning-team', 'emergency-telephone-list'],
    children: [
      { title: 'System/School Safety Planning Team', route: 'system-school-safety-planning-team' },
      { title: 'Emergency Telephone List(s)', route: 'emergency-telephone-list' }
    ]
  },
  {
    title: 'NIMS & ICS',
    icon: 'ThumbsUp',
    activeRoutes: ['staff-skills-survey-inventory', 'incident-command-system-roles'],
    children: [
      { title: 'Staff Skills Survey & Inventory', route: 'staff-skills-survey-inventory' },
      { title: 'Incident Command System Roles', route: 'incident-command-system-roles' }
    ]
  },
  {
    title: 'List of Materials',
    icon: 'InspectionPanel',
    activeRoutes: ['list-of-materials-equipment', 'basic-resources'],
    children: [
      { title: 'List of Materials and Equipment', route: 'list-of-materials-equipment' },
      { title: 'Basic Resources Needed', route: 'basic-resources' }
    ]
  },
  {
    title: 'Hazard-Specific Annexes',
    icon: 'Layers2',
    activeRoutes: ['hazard-school-ground', 'hazard-classroom', 'hazard-evacuation-routes', 'hazard-neighborhoods', 'analysis-identified-hazards'],
    children: [
      { title: 'Identification of Hazard (School Grounds)', route: 'hazard-school-ground' },
      { title: 'Identification of Hazard (Classroom)', route: 'hazard-classroom' },
      { title: 'Identification of Hazard (Along Evacuation Routes)', route: 'hazard-evacuation-routes' },
      { title: 'Identification of Hazard (Neighborhoods & Communities)', route: 'hazard-neighborhoods' },
      { title: 'Analysis of Identified Hazards', route: 'analysis-identified-hazards' }
    ]
  },
  {
    title: 'Functional Annexes',
    icon: 'Layers',
    activeRoutes: ['communication', 'evacuation', 'heightened-awareness', 'lockdown', 'secure-perimeter', 'secure-area-responsibility', 'shelter-in-place', 'after-school-activities', 'crisis-management'],
    children: [
      { title: 'Communication', route: 'communication' },
      { title: 'Evacuation', route: 'evacuation' },
      { title: 'Heightened Awareness', route: 'heightened-awareness' },
      { title: 'Lockdown', route: 'lockdown' },
      { title: 'Secure Perimeter', route: 'secure-perimeter' },
      { title: 'Secure Your Area of Responsibility', route: 'secure-area-responsibility' },
      { title: 'Shelter-in-Place', route: 'shelter-in-place' },
      { title: 'After-School-Activities', route: 'after-school-activities' },
      { title: 'Crisis-Management', route: 'crisis-management' }
    ]
  },
  {
    title: 'Drill, Training, & Collaboration Reports',
    icon: 'ClipboardCheck',
    activeRoutes: ['drills-training-collaboration-reports', 'mandatory-training-resources'],
    children: [
      { title: 'Drill, Training, Crisis, & Collaboration Reports', route: 'drills-training-collaboration-reports' },
      { title: 'Mandatory Training Resources', route: 'mandatory-training-resources' }
    ]
  },
  {
    title: 'Central Office',
    icon: 'ScrollText',
    activeRoutes: ['central-office-eop', 'district-coordinator-training-log'],
    children: [
      { title: 'Central Office EOP', route: 'central-office-eop' },
      { title: 'District Coordinator Training Log', route: 'district-coordinator-training-log' }
    ]
  }
];

const msSidebarEntries: IEopSidebarEntry[] = [
  { title: 'EOP Summary', route: 'summary', icon: 'Home' },
  { title: 'Mission Statement', route: 'mission-statement', icon: 'Handshake' },
  {
    title: 'Emergency Contacts',
    icon: 'BookUser',
    activeRoutes: ['district-emergency-contact-information', 'community-emergency-contact-information', 'campus-emergency-response-team-contact-list'],
    children: [
      { title: 'District Emergency Contact Information', route: 'district-emergency-contact-information' },
      { title: 'Community Emergency Contact Information', route: 'community-emergency-contact-information' },
      { title: 'Campus Emergency Response Team Contact List', route: 'campus-emergency-response-team-contact-list' }
    ]
  },
  {
    title: 'Evacuation & Reunification',
    icon: 'Layers2',
    activeRoutes: ['evacuation-areas', 'reunification-plan', 'reunification-areas'],
    children: [
      { title: 'Evacuation Areas', route: 'evacuation-areas' },
      { title: 'Reunification Plan', route: 'reunification-plan' },
      { title: 'Reunification Areas', route: 'reunification-areas' }
    ]
  },
  { title: 'ADA/Special Needs Considerations Plan', route: 'ada-special-needs-considerations-plan', icon: 'Layers' },
  {
    title: 'Campus Specific Emergency Response Plans',
    icon: 'FileText',
    activeRoutes: ['general-response-duties', 'fire-explosion', 'fire-explosion-near-the-school', 'severe-weather', 'intruder-lockdown-controlled-movement', 'earthquake', 'bomb-threat', 'gas-leak-gas-odor', 'hazardous-materials', 'after-school-care', 'athletic-zone-plan'],
    children: [
      { title: 'General Response Duties', route: 'general-response-duties' },
      { title: 'Fire / Explosion', route: 'fire-explosion' },
      { title: 'Fire / Explosion Near The School', route: 'fire-explosion-near-the-school' },
      { title: 'Severe Weather', route: 'severe-weather' },
      { title: 'Intruder / Lockdown / Controlled Movement', route: 'intruder-lockdown-controlled-movement' },
      { title: 'Earthquake', route: 'earthquake' },
      { title: 'Bomb Threat', route: 'bomb-threat' },
      { title: 'Gas Leak / Gas Odor', route: 'gas-leak-gas-odor' },
      { title: 'Hazardous Materials', route: 'hazardous-materials' },
      { title: 'After School Care', route: 'after-school-care' },
      { title: 'Athletic Zone Plan', route: 'athletic-zone-plan' }
    ]
  },
  {
    title: 'Drill, Training, and Collaboration Reports',
    icon: 'ClipboardCheck',
    activeRoutes: ['drills-training-collaboration-reports', 'sro-training-resources', 'sso-training-resources'],
    children: [
      { title: 'Drill, Training, & Collaboration Reports', route: 'drills-training-collaboration-reports' },
      { title: 'SRO Training Resources', route: 'sro-training-resources' },
      { title: 'SSO Training Resources', route: 'sso-training-resources' }
    ]
  }
];

const gaSidebarEntries: IEopSidebarEntry[] = [
  { title: 'EOP Summary', route: 'summary', icon: 'Home' },
  { title: 'Purpose', route: 'purpose', icon: 'ScrollText' },
  {
    title: 'School Information',
    icon: 'BookUser',
    activeRoutes: ['school-leadership', 'incident-command-system', 'school-demographics', 'after-hours-contact', 'administrator-names-and-positions', 'school-safety-committee', 'number-of-student-staff-rooms'],
    children: [
      { title: 'School Leadership', route: 'school-leadership' },
      { title: 'Incident Command System', route: 'incident-command-system' },
      { title: 'School Demographics', route: 'school-demographics' },
      { title: 'After-Hours Contacts', route: 'after-hours-contact' },
      { title: 'Administrator Names and Positions', route: 'administrator-names-and-positions' },
      { title: 'School Safety Committee', route: 'school-safety-committee' },
      { title: 'Number of Student, Staff, Rooms', route: 'number-of-student-staff-rooms' }
    ]
  },
  {
    title: 'Facility Information',
    icon: 'InspectionPanel',
    activeRoutes: ['shelter-information', 'hazardous-materials-in-school-facility'],
    children: [
      { title: 'Shelter Information', route: 'shelter-information' },
      { title: 'Hazardous Materials in School Facility', route: 'hazardous-materials-in-school-facility' }
    ]
  },
  {
    title: 'Evacuation & Transportation',
    icon: 'Layers2',
    activeRoutes: ['transportation-needs', 'evacuation-routes-reunification-sites-and-staging-areas', 'emergency-evacuation-kits', 'special-needs-consideration'],
    children: [
      { title: 'Transportation Needs', route: 'transportation-needs' },
      { title: 'Evacuation Routes, Reunification Sites, and Staging Areas', route: 'evacuation-routes-reunification-sites-and-staging-areas' },
      { title: 'Emergency Evacuation Kits', route: 'emergency-evacuation-kits' },
      { title: 'Special Needs Consideration', route: 'special-needs-consideration' }
    ]
  },
  {
    title: 'Emergency Preparedness',
    icon: 'ClipboardCheck',
    activeRoutes: ['emergency-utilities-checklist', 'drills', 'cpr-first-aid-aed'],
    children: [
      { title: 'Emergency Utilities Checklist', route: 'emergency-utilities-checklist' },
      { title: 'Drills', route: 'drills' },
      { title: 'CPR/First Aid/AED', route: 'cpr-first-aid-aed' }
    ]
  },
  {
    title: 'Security & Access',
    icon: 'Layers',
    activeRoutes: ['security-issues-in-school-safety-zone', 'school-visitors', 'student-sign-in-and-out-procedure', 'medications', 'emergency-status-cards'],
    children: [
      { title: 'Security Issues in School Safety Zone', route: 'security-issues-in-school-safety-zone' },
      { title: 'School Visitors', route: 'school-visitors' },
      { title: 'Student Sign In and Out Procedure', route: 'student-sign-in-and-out-procedure' },
      { title: 'Medications', route: 'medications' },
      { title: 'Emergency Status Cards', route: 'emergency-status-cards' }
    ]
  },
  { title: 'Custom Pages', route: 'custom-pages', icon: 'FileText' }
];

function flattenSidebarEntries(entries: IEopSidebarEntry[]): IEopPageConfig[] {
  return entries.flatMap(entry => {
    if (entry.children?.length) {
      return entry.children.map(child => ({
        title: child.title,
        route: child.route
      }));
    }

    if (entry.route) {
      return [{ title: entry.title, route: entry.route }];
    }

    return [];
  });
}

const baseEopPages = flattenSidebarEntries(alSidebarEntries);

export const EOP_PAGES_BY_PLAN: Record<EopPlanSlug, IEopPageConfig[]> = {
  'al': baseEopPages,
  'ms': flattenSidebarEntries(msSidebarEntries),
  'ga': flattenSidebarEntries(gaSidebarEntries)
};

export const EOP_SIDEBAR_BY_PLAN: Record<EopPlanSlug, IEopSidebarEntry[]> = {
  'al': alSidebarEntries,
  'ms': msSidebarEntries,
  'ga': gaSidebarEntries
};

// Sections that have EOP Assistant suggestions. AL offers them everywhere, GA
// has none yet, and MS only on the sections listed here.
export const ASSISTANT_SECTIONS_BY_PLAN: Record<EopPlanSlug, ESectionTypes[] | 'all'> = {
  al: 'all',
  ms: [
    ESectionTypes['Mission Statement'],
    ESectionTypes['ADA/Special Needs Considerations Plan'],
    ESectionTypes['General Response Duties'],
    ESectionTypes['Fire / Explosion'],
    ESectionTypes['Fire / Explosion Near The School'],
    ESectionTypes['Severe Weather'],
    ESectionTypes['Intruder / Lockdown / Controlled Movement'],
    ESectionTypes['Earthquake'],
    ESectionTypes['Bomb Threat'],
    ESectionTypes['Gas Leak / Gas Odor'],
    ESectionTypes['Hazardous Materials'],
    ESectionTypes['After School Care']
  ],
  ga: []
};

// Plans that still open the per-page introduction modal. MS and GA do not.
export const PLANS_WITH_INTRO_MODAL: EopPlanSlug[] = ['al'];

export function isSupportedEopPlan(value: string | null | undefined): value is EopPlanSlug {
  return !!value && SUPPORTED_EOP_PLANS.includes(value as EopPlanSlug);
}

export function getPlanSlugFromUrl(url: string): EopPlanSlug {
  const normalizedUrl = url.replace(/\/+$/, '');
  const segments = normalizedUrl.split('/');
  const matchedPlan = segments.find(isSupportedEopPlan);

  return matchedPlan ?? DEFAULT_EOP_PLAN;
}

export function getBuildingIdFromUrl(url: string): string | null {
  const normalizedUrl = url.replace(/\/+$/, '');
  const segments = normalizedUrl.split('/');
  const buildingIndex = segments.findIndex(segment => segment === 'building');

  if (buildingIndex === -1) {
    return null;
  }

  return segments[buildingIndex + 1] ?? null;
}

export function buildPlanPageUrl(plan: string, buildingId: string, pagePath: string): string {
  const resolvedPlan = isSupportedEopPlan(plan) ? plan : DEFAULT_EOP_PLAN;
  return `/plans/${resolvedPlan}/building/${buildingId}/${pagePath}`;
}

export function getEopPages(plan: string): IEopPageConfig[] {
  if (isSupportedEopPlan(plan)) {
    return EOP_PAGES_BY_PLAN[plan];
  }

  return EOP_PAGES_BY_PLAN[DEFAULT_EOP_PLAN];
}

export function getEopSidebar(plan: string): IEopSidebarEntry[] {
  if (isSupportedEopPlan(plan)) {
    return EOP_SIDEBAR_BY_PLAN[plan];
  }

  return EOP_SIDEBAR_BY_PLAN[DEFAULT_EOP_PLAN];
}

export function hasEopAssistant(plan: string, sectionId: number | null | undefined): boolean {
  if (sectionId === null || sectionId === undefined) {
    return false;
  }

  const sections = ASSISTANT_SECTIONS_BY_PLAN[isSupportedEopPlan(plan) ? plan : DEFAULT_EOP_PLAN];

  return sections === 'all' || sections.includes(sectionId);
}

export function hasIntroModal(plan: string): boolean {
  return isSupportedEopPlan(plan) && PLANS_WITH_INTRO_MODAL.includes(plan);
}
